import { useState } from 'react';
import { Modal } from '@mantine/core';
import { Button } from './Button';
import { Icon } from './Icon';
import { fmt, sourceLabel } from '@/lib/format';
import { RECRUITMENT_ORIGINS, type PreviewOutput, type RecruitmentOrigin } from '@/api/imports';

type Props = {
  opened: boolean;
  onClose: () => void;
  onConfirm: (origin: { recruitmentOrigin: RecruitmentOrigin; recruitmentPartner?: string }) => void;
  running: boolean;
  filename: string;
  source: string;
  sheet?: string | null;
  preview?: PreviewOutput | null;
  /** Vrai pour un import déjà tenté (CANCELLED / FAILED) que l'on relance. */
  relaunch?: boolean;
};

/**
 * Confirmation avant de lancer un import.
 *
 * L'aperçu est une simulation : il n'écrit rien, on peut le rejouer sans fin.
 * Le lancement, lui, écrit dans un journal en **ajout seul** — ce qui y entre
 * n'en sort plus. Une erreur ne se « supprime » pas, elle se corrige par de
 * nouveaux événements. Un tel geste ne doit pas partir sur un simple clic :
 * l'écran rappelle ce qui va être écrit, sur quelle interprétation, et ce qui
 * sera irréversible.
 */
export function ConfirmRunImportModal({
  opened,
  onClose,
  onConfirm,
  running,
  filename,
  source,
  sheet,
  preview,
  relaunch = false,
}: Props) {
  // L'origine se déclare ICI, au moment du lancement : c'est le seul instant où
  // l'opérateur a sous les yeux le fichier, sa source et son aperçu. Le fichier
  // lui-même ne permet pas de la deviner — dans l'export Orange, la colonne
  // « partenaire » porte la même valeur sur toutes les lignes.
  const [origin, setOrigin] = useState<RecruitmentOrigin>('ORANGE_PARTENAIRE');
  const [partner, setPartner] = useState('');

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size={560}
      centered
      title={relaunch ? "Relancer l'import ?" : "Lancer l'import ?"}
    >
      <dl className="confirm-run">
        <dt>Fichier</dt>
        <dd style={{ fontFamily: 'var(--mono)' }}>{filename}</dd>
        <dt>Source</dt>
        <dd>{sourceLabel(source)}</dd>
        {sheet ? (
          <>
            <dt>Feuille</dt>
            <dd style={{ fontFamily: 'var(--mono)' }}>{sheet}</dd>
          </>
        ) : null}
        {preview ? (
          <>
            <dt>Aperçu</dt>
            <dd>
              {fmt(preview.accepted)}/{fmt(preview.sampleSize)} lignes simulées sans écriture
              {preview.rejected > 0 ? ` · ${fmt(preview.rejected)} rejet(s)` : ''}
            </dd>
          </>
        ) : null}
      </dl>

      <div className="field" style={{ marginBottom: 14 }}>
        <label>
          Qui a enrôlé les commerçants de ce lot ?
          <span className="muted" style={{ fontWeight: 600 }}>
            {' '}— s'applique à toutes les lignes du fichier
          </span>
        </label>
        <select value={origin} onChange={(e) => setOrigin(e.target.value as RecruitmentOrigin)}>
          {RECRUITMENT_ORIGINS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {origin !== 'MAFA_TERRAIN' && (
        <div className="field" style={{ marginBottom: 14 }}>
          <label>
            Nom du partenaire
            <span className="muted" style={{ fontWeight: 600 }}> — facultatif</span>
          </label>
          <input
            value={partner}
            onChange={(e) => setPartner(e.target.value)}
            placeholder="Ex : Groupe X"
          />
        </div>
      )}

      <div className="confirm-warn">
        <Icon name="alert" size={16} />
        <div>
          <strong>Cette action ne se défait pas.</strong> Les événements écrits entrent dans un
          journal en ajout seul : ils ne peuvent pas être supprimés. Une erreur d'interprétation se
          corrige en ingérant de nouveaux événements, jamais en effaçant les précédents.
          {relaunch
            ? " Les lignes déjà acceptées lors de la tentative précédente ne seront pas dupliquées : elles portent le même identifiant."
            : ''}
        </div>
      </div>

      <p className="muted" style={{ fontSize: 12.5, fontWeight: 600, marginTop: 12 }}>
        L'import s'exécute en arrière-plan. Vous pourrez l'arrêter en cours de route, mais les
        lignes déjà écrites resteront.
      </p>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
        <Button variant="ghost" onClick={onClose} disabled={running}>
          Annuler
        </Button>
        <Button
          variant="primary"
          icon="play"
          onClick={() =>
            onConfirm({ recruitmentOrigin: origin, recruitmentPartner: partner.trim() || undefined })
          }
          loading={running}
        >
          {running ? 'Lancement…' : relaunch ? "Relancer l'import" : "Oui, lancer l'import"}
        </Button>
      </div>
    </Modal>
  );
}
